import { Component, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { DxFormComponent } from 'devextreme-angular';
import { DxSelectBoxTypes } from 'devextreme-angular/ui/select-box';
import { ToastrService } from 'ngx-toastr';
import { map, filter, tap, switchMap, of, finalize } from 'rxjs';
import { PageStateFlags } from 'src/app/constants/page-state.constants';
import { StandardTrainingCategoryCreationAndUpdateModel } from 'src/app/models/standard-training-category/standard-training-category-creation-and-update.model';
import { StandardTrainingCategoryOverviewModel } from 'src/app/models/standard-training-category/standard-training-category-overview.model';
import { StandardTrainingCategoryTranslationModel } from 'src/app/models/standard-training-category/standard-training-category-translation.model';
import { StandardTrainingCategoryTypeModel } from 'src/app/models/standard-training-category/standard-training-category-type.model';
import { StandardTrainingCategoryModel } from 'src/app/models/standard-training-category/standard-training-category.model';
import { FileUtilService } from 'src/app/services/file-util.service';
import { LoadingService } from 'src/app/services/loading.service';
import { PageStateService } from 'src/app/services/page-state.service';
import { StandardTrainingCategoryAdminService } from 'src/app/services/training-categories/standard-training-category-admin.service';
import { UserContextService } from 'src/app/services/user-context.service';

@Component({
  selector: 'app-edit-standard-category',
  standalone: false,
  templateUrl: './edit-standard-category.component.html',
  styleUrl: './edit-standard-category.component.scss',
})
export class EditStandardCategoryComponent {
  @ViewChild('form', { static: false })
  form!: DxFormComponent;

  isAuthenticated = false;
  isLoaded = false;

  editingMode = false;
  currentCategory: StandardTrainingCategoryModel | null = null;

  categories: StandardTrainingCategoryOverviewModel[] = [];
  parentCandidates: StandardTrainingCategoryOverviewModel[] = [];

  selectedLanguageCode = 'en';

  readonly languageOptions = [
    { id: 'en', name: 'English' },
    { id: 'de', name: 'Deutsch' },
    { id: 'es', name: 'Español' },
    { id: 'fr', name: 'Français' },
    { id: 'it', name: 'Italiano' },
    { id: 'nl', name: 'Nederlands' },
    { id: 'pl', name: 'Polski' },
  ];

  formData: StandardTrainingCategoryCreationAndUpdateModel =
    this.createEmptyModel();

  selectedTranslation!: StandardTrainingCategoryTranslationModel;

  readonly typeOptions = [
    {
      id: StandardTrainingCategoryTypeModel.PrivateInsurance,
      name: this.translateService.instant(
        'STANDARD-CATEGORIES.STANDARD-CATEGORY-TYPES.PRIVATE-INSURANCES'
      ),
    },
    {
      id: StandardTrainingCategoryTypeModel.CorporateInsurance,
      name: this.translateService.instant(
        'STANDARD-CATEGORIES.STANDARD-CATEGORY-TYPES.CORPORATE-INSURANCES'
      ),
    },
  ];

  typeSelectOptions: DxSelectBoxTypes.Properties = {
    showClearButton: false,
    stylingMode: 'outlined',
    displayExpr: 'name',
    valueExpr: 'id',
    dataSource: this.typeOptions,
    placeholder: '',
    onValueChanged: (e) => this.onTypeChanged(e),
  };

  parentSelectOptions: DxSelectBoxTypes.Properties = {
    showClearButton: true,
    stylingMode: 'outlined',
    displayExpr: 'name',
    valueExpr: 'id',
    dataSource: [],
    placeholder: '',
    searchEnabled: true,
    searchExpr: 'name',
    onValueChanged: (e) => this.onParentChanged(e),
  };

  languageSelectOptions: DxSelectBoxTypes.Properties = {
    showClearButton: false,
    stylingMode: 'outlined',
    displayExpr: 'name',
    valueExpr: 'id',
    dataSource: this.languageOptions,
    placeholder: '',
    onValueChanged: (e) => this.onLanguageChanged(e),
  };

  constructor(
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private loadingService: LoadingService,
    private standardTrainingCategoryService: StandardTrainingCategoryAdminService,
    private toastrService: ToastrService,
    private translateService: TranslateService,
    private pageStateService: PageStateService,
    private fileUtilService: FileUtilService,
    private userContext: UserContextService
  ) {}

  ngOnInit(): void {
    this.activatedRoute.params
      .pipe(
        map((params) => params['id']),
        filter((id) => !!id),
        tap((id) => {
          this.editingMode = id !== 'new';
          this.loadCategory(id);
        })
      )
      .subscribe();
  }

  onLoginSuccess(): void {
    this.isAuthenticated = true;
    this.loadCategory(this.activatedRoute.snapshot.params['id']);
  }

  private loadCategory(id: string): void {
    this.isLoaded = false;

    this.loadingService
      .load(
        this.standardTrainingCategoryService
          .getStandardTrainingCategoriesOverview(
            this.userContext.currentPersonCulture ?? 'en'
          )
          .pipe(
            switchMap((categories) => {
              if (!this.editingMode) {
                return of({
                  category: null,
                  categories,
                });
              }

              return this.standardTrainingCategoryService
                .getStandardTrainingCategory(id)
                .pipe(
                  map((category) => ({
                    category,
                    categories,
                  }))
                );
            })
          )
      )
      .pipe(
        finalize(() => {
          this.isLoaded = true;
        })
      )
      .subscribe({
        next: ({ category, categories }) => {
          this.isAuthenticated = true;

          this.categories = categories;
          this.currentCategory = category;

          if (!category) {
            this.formData = this.createEmptyModel();
            this.updateSelectedTranslation();
            this.refreshParentCandidates();
            return;
          }

          const imageFileUrl = category.imageFile?.fileUrl;

          if (imageFileUrl) {
            this.loadingService
              .load(this.fileUtilService.avatarUrlToBase64(imageFileUrl))
              .subscribe((base64) => {
                const mimeType =
                  this.fileUtilService.getImageMimeType(imageFileUrl);

                this.formData.image =
                  `data:${mimeType};base64,${base64}`;
              });
          }

          this.formData.type = category.type;
          this.formData.parentId = category.parentId;
          this.formData.translations = category.translations.map(
            (translation) => ({
              languageCode: translation.languageCode,
              name: translation.name,
              description: translation.description,
              descriptionExtended: translation.descriptionExtended,
            })
          );

          this.updateSelectedTranslation();
          this.refreshParentCandidates();
        },
        error: () => {
          this.isAuthenticated = false;
        },
      });
  }

  private updateSelectedTranslation(): void {
    let translation = this.formData.translations.find(
      (x) => x.languageCode === this.selectedLanguageCode
    );

    if (!translation) {
      translation = {
        languageCode: this.selectedLanguageCode,
        name: '',
        description: '',
        descriptionExtended: '',
      };

      this.formData.translations.push(translation);
    }

    this.selectedTranslation = translation;
  }

  onLanguageChanged(e: any): void {
    this.selectedLanguageCode = e.value;
    this.updateSelectedTranslation();
  }

  onTypeChanged(e: any): void {
    this.formData.type = e.value;
    this.refreshParentCandidates();

    if (!this.formData.parentId) {
      return;
    }

    const isParentStillValid = this.parentCandidates.some(
      (x) => x.id === this.formData.parentId
    );

    if (!isParentStillValid) {
      this.formData.parentId = null;
    }
  }

  onParentChanged(e: any): void {
    this.formData.parentId = e.value;
  }

  submit(): void {
    const formValid = this.form.instance.validate().isValid;

    if (!formValid) {
      this.toastrService.error(
        this.translateService.instant(
          'VALIDATIONS.VALUES-NOT-VALID'
        )
      );
      return;
    }

    const model: StandardTrainingCategoryCreationAndUpdateModel = {
      ...this.formData,
      translations: this.formData.translations.filter(
        translation => !!translation.name?.trim()
      ),
      image: this.formData.image
        ? this.formData.image.split(',')[1]
        : null,
    };

    if (this.editingMode && this.currentCategory) {
      this.loadingService
        .load(
          this.standardTrainingCategoryService.updateStandardTrainingCategory(
            this.currentCategory.id,
            model
          )
        )
        .subscribe(() => {
          this.toastrService.success(
            this.translateService.instant(
              'STANDARD-CATEGORIES.ADMIN.SAVED-STANDARD-CATEGORY'
            )
          );
        });

      return;
    }

    this.loadingService
      .load(
        this.standardTrainingCategoryService
          .addStandardTrainingCategory(model)
      )
      .subscribe((created) => {
        this.toastrService.success(
          this.translateService.instant(
            'STANDARD-CATEGORIES.ADMIN.ADDED-STANDARD-CATEGORY'
          )
        );

        this.router.navigate([
          'standard-categories',
          created.id,
        ]);
      });
  }

  deleteCategory(): void {
    if (!this.currentCategory) {
      return;
    }

    this.loadingService
      .load(
        this.standardTrainingCategoryService.deleteStandardTrainingCategory(
          this.currentCategory.id
        )
      )
      .subscribe({
        next: () => {
          this.toastrService.success(
            this.translateService.instant(
              'STANDARD-CATEGORIES.ADMIN.DELETED-STANDARD-CATEGORY'
            )
          );

          this.navigateBack();
        },
        error: () => {
          this.toastrService.error(
            this.translateService.instant('COMMON.UNKNOWN-ERROR')
          );
        },
      });
  }

  navigateBack(): void {
    this.pageStateService.setFlag(
      PageStateFlags.RestoreStandardTrainingCategoriesOverview
    );

    this.router.navigate(['standard-categories']);
  }

  onDeleteItemClick(): void {
    this.deleteCategory();
  }

  private refreshParentCandidates(): void {
    const excludedId = this.currentCategory?.id;

    this.parentCandidates = this.categories.filter(
      (x) =>
        x.type === this.formData.type &&
        !x.parentId &&
        x.id !== excludedId
    );

    this.parentSelectOptions = {
      ...this.parentSelectOptions,
      dataSource: this.parentCandidates.map((category) => ({
        ...category,
        name: category.name || '-',
      })),
    };
  }

  private createEmptyModel(): StandardTrainingCategoryCreationAndUpdateModel {
    return {
      type: StandardTrainingCategoryTypeModel.PrivateInsurance,
      parentId: null,
      image: '',
      translations: [
        {
          languageCode: 'en',
          name: '',
          description: '',
          descriptionExtended: '',
        },
        {
          languageCode: 'de',
          name: '',
          description: '',
          descriptionExtended: '',
        },
      ],
    };
  }

  onStandardCategoryImageSelected(files: any): void {
    const file = files[0];
    const reader = new FileReader();

    reader.onload = () => {
      this.formData.image = reader.result!.toString();
    };

    reader.readAsDataURL(file);
  }

  deleteStandardCategory(): void {
    if (!this.currentCategory) {
      return;
    }

    this.loadingService.load(
        this.standardTrainingCategoryService.deleteStandardTrainingCategory(
          this.currentCategory.id
        )
      )
      .subscribe(() => {
        this.toastrService.success(
          this.translateService.instant(
            'STANDARD-CATEGORIES.ADMIN.DELETED-STANDARD-CATEGORY'
          )
        );
        this.navigateBack();
      });
  }
}